/**
 * `DecisionCard` — COMMS.app's interactive decision affordance. Renders one live `game_decision`
 * (Brace, Overwatch, or an authored offer — anything riding the generic cross-actor prompt channel)
 * as a sticky, table-visible, resolvable card. Ephemeral: sourced from `GameSession.decisions` (fed
 * by `game_decision`/`game_decision_resolved` on `/ws/game`), never a `FeedEntry`.
 *
 * **The layout** (`comms-attack-reaction-cards` 4.1, the design's Brace card):
 *
 *     REACTION · Little Red Bus                 Bret DECIDES
 *     Brace against Ronin's hit?
 *     [TAKE IT 8 KIN]  [BRACE 4 KIN]
 *     Uses your reaction · next turn: 1 quick action, no reactions
 *     [ Brace ]  [ Decline ]
 *
 * The question, tiles and summary render only for a decision passing `hasReactionStakes`
 * (`incomingDamage`, `title` and `summary`: Brace). Any other decision (Overwatch, System Trauma,
 * Intercede, an authored damage offer) shows its authored sentence (`kind`) in the question's place,
 * plus its `summary` if it has one. Generic over the decision: the title,
 * options and summary all come off the wire, never hardcoded per-reaction copy.
 *
 * **Buttons gated `whoSlotIds.includes(actorId) || role === "gm"`** — any controlling player or any GM can
 * resolve it; everyone else (a spectator player) sees the card for awareness with inert (disabled)
 * controls, never a silent no-op click. `who` on the wire is a COMBATANT id (echoes
 * `NeedsDecision.who`); `whoSlotIds` are that combatant's controlling slot ids, resolved server-side
 * (`sendDecisionPrompt`'s `deciderSlot`) since COMMS has no encounter state to do it client-side.
 *
 * **Two homes, two button behaviors.** The prompt/answer round-trip lives on `/ws/encounter`, but
 * the card itself is broadcast on `/ws/game` — so it can render anywhere COMMS mounts, but can only
 * ACT where a live encounter controller exists to re-submit the answer:
 *  - **COMBAT's COMMS** (COMBAT.app on the shell's stage, a live encounter controller exists) —
 *    `answerPrompt` is threaded down from `CombatApp` via a `CommsApp` prop and the decider's
 *    buttons re-submit the reaction over the encounter socket, exactly like `PromptModal`.
 *  - **the shell's own COMMS** (a narrative stage, no encounter controller mounted) —
 *    `answerPrompt` is `undefined`, so a decider is told to respond once the GM puts the fight on
 *    the stage (players never navigate into a fight, `decisions/0203` point 2); a non-decider
 *    spectator sees only the card's information — awareness-only there, by design.
 *
 * **Never un-dismissable** (`stuck-brace`). The card answers ITS OWN `promptId`
 * (`RemoteEncounterController.answerDecision`), never whichever prompt is newest. If this client no
 * longer holds that prompt — it was withdrawn, or lost across a reconnect or a server restart — a
 * GM's click asks the server to answer from its own record, and the server resolves it or clears
 * the card. Anyone else's click flips the card to "no longer open" with a Dismiss button. After a
 * click the buttons rest for {@link ANSWER_SETTLE_MS} so a double-click doesn't send twice.
 *
 * Split into the hook-reading shell and a pure {@link DecisionCardView} so the view renders under
 * test without a session provider or router (`DecisionCard.render.test.tsx`).
 */
import { useEffect, useState } from "react";
import type { GameDecisionMessage, PromptOption } from "@lancer-vtt/shared";
import { useGameSessionContext } from "../../lib/game/GameSessionProvider";
import type { SlotPresence } from "../../lib/game/useGameSession";
import { DECLINE_OPTION_ID, ReactionStakes, acceptOption, hasReactionStakes, reactionQuestion } from "../../lib/encounter/ReactionStakes";
import { isSplitOption } from "../../lib/encounter/coveringFireSplit";
import { LINK_DOWN_NOTE } from "../../lib/roomWs";
import "./decision-card.css";

/** How long the buttons rest after a click, waiting for the server to clear or re-offer the card. */
export const ANSWER_SETTLE_MS = 4000;

export function DecisionCard({
  decision,
  answerDecision,
  onHoverOption,
}: {
  decision: GameDecisionMessage;
  /** Present only where a live `/ws/encounter` controller is mounted (COMBAT.app's COMMS). */
  answerDecision?: (promptId: string, optionId: string) => "sent" | "gone" | "offline";
  /** The board's highlight for a hovered option (`null` on leave); present only beside a board. */
  onHoverOption?: (decision: GameDecisionMessage, option: PromptOption | null) => void;
}) {
  const session = useGameSessionContext();
  const { actorId, role, slots, dismissDecision } = session;
  return (
    <DecisionCardView
      decision={decision}
      viewer={{ actorId, role }}
      slots={slots}
      {...(answerDecision !== undefined ? { answerPrompt: (optionId: string) => {
        const outcome = answerDecision(decision.promptId, optionId);
        return outcome === "offline" ? outcome : outcome === "sent";
      } } : {})}
      onDismiss={() => dismissDecision(decision.promptId)}
      {...(onHoverOption !== undefined ? { onHoverOption: (option: PromptOption | null) => onHoverOption(decision, option) } : {})}
    />
  );
}

/**
 * Who decides, as the header's right-hand label: `YOU DECIDE` when the viewer is the decider (a
 * controlling slot, or the GM on a unit no player controls); `GM DECIDES` for a unit no player
 * controls; otherwise the owner-of-record's slot name (`Bret DECIDES`).
 */
export function deciderLabel(
  decision: Pick<GameDecisionMessage, "whoSlotIds">,
  viewer: { actorId: string | null; role: string | null },
  slots: readonly Pick<SlotPresence, "id" | "name" | "role">[],
): string {
  const playerSlots = (decision.whoSlotIds ?? []).filter((id) => slots.find((s) => s.id === id)?.role !== "gm");
  const viewerControls = viewer.actorId !== null && (decision.whoSlotIds?.includes(viewer.actorId) ?? false);
  if (viewerControls || (playerSlots.length === 0 && viewer.role === "gm")) return "YOU DECIDE";
  const owner = playerSlots[0];
  if (owner === undefined) return "GM DECIDES";
  return `${slots.find((s) => s.id === owner)?.name ?? owner} DECIDES`;
}

export function DecisionCardView({
  decision,
  viewer,
  slots,
  answerPrompt,
  onDismiss,
  onHoverOption,
}: {
  decision: GameDecisionMessage;
  viewer: { actorId: string | null; role: string | null };
  slots: readonly Pick<SlotPresence, "id" | "name" | "role">[];
  /**
   * Answer this card's decision; `false` when there is nothing left here to answer, `"offline"`
   * when the link is down and the answer was not sent (the card stays open, with a note).
   */
  answerPrompt?: (optionId: string) => boolean | "offline";
  /** Drop the card locally — offered once a click finds the decision no longer open. */
  onDismiss?: () => void;
  /** An option button was hovered or focused (`null` when it is left): a cell or unit card's board highlight. */
  onHoverOption?: (option: PromptOption | null) => void;
}) {
  const [gone, setGone] = useState(false);
  const [notSent, setNotSent] = useState(false);
  const [settling, setSettling] = useState(false);
  useEffect(() => {
    if (!settling) return;
    const t = setTimeout(() => setSettling(false), ANSWER_SETTLE_MS);
    return () => clearTimeout(t);
  }, [settling]);
  // `who` is a COMBATANT id (not a slot id) — `whoSlotIds` are the decider's controlling slots, the
  // field this gate must compare against `actorId`. Under shared control every controller of the
  // deciding unit may answer, and whoever clicks first decides.
  const decider =
    (viewer.actorId !== null && (decision.whoSlotIds?.includes(viewer.actorId) ?? false)) || viewer.role === "gm";
  const unitName = decision.names[decision.who] ?? decision.who;
  const sourceName = decision.source !== undefined ? decision.names[decision.source] : undefined;
  const accept = acceptOption(decision.options);
  const { title } = decision;

  return (
    <div className="comms-decision" role="dialog" aria-label="live decision">
      <div className="comms-decision-head">
        <span className="comms-decision-unit">
          {title !== undefined ? "REACTION" : "DECISION"} · {unitName}
        </span>
        <span className="comms-decision-decider">{deciderLabel(decision, viewer, slots)}</span>
      </div>
      {hasReactionStakes(decision) ? (
        <>
          <div className="comms-decision-question">{reactionQuestion(decision.title, sourceName)}</div>
          <ReactionStakes dmg={decision.incomingDamage} acceptLabel={accept?.label ?? decision.title} summary={decision.summary} />
        </>
      ) : (
        <>
          <div className="comms-decision-prompt">{decision.kind}</div>
          {decision.summary !== undefined && <p className="comms-decision-summary">{decision.summary}</p>}
        </>
      )}
      {gone ? (
        <div className="comms-decision-gone">
          <p className="comms-decision-summary">
            No longer open here: this screen lost its copy of the decision (a reconnect or a server restart), so it cannot send an answer. If the
            decision is still live, the GM can answer it.
          </p>
          {onDismiss !== undefined && (
            <button type="button" className="comms-decision-join" onClick={onDismiss}>
              Dismiss
            </button>
          )}
        </div>
      ) : answerPrompt !== undefined ? (
        <div className="comms-decision-opts">
          {/* A Covering Fire offer's path cells are picked on the board (the prompt window's "Pick where along the path"), not here. */}
          {decision.options.filter((opt) => !isSplitOption(opt.id)).map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={opt.id === DECLINE_OPTION_ID ? "comms-decision-opt" : "comms-decision-opt comms-decision-opt-accept"}
              disabled={!decider || settling}
              onMouseEnter={() => onHoverOption?.(opt)}
              onMouseLeave={() => onHoverOption?.(null)}
              onFocus={() => onHoverOption?.(opt)}
              onBlur={() => onHoverOption?.(null)}
              onClick={() => {
                if (!decider || settling) return;
                const outcome = answerPrompt(opt.id);
                setNotSent(outcome === "offline");
                if (outcome === "offline") return;
                if (outcome) setSettling(true);
                else setGone(true);
              }}
            >
              {opt.label}
            </button>
          ))}
          {notSent && <p className="comms-decision-summary comms-decision-notsent">{LINK_DOWN_NOTE}</p>}
        </div>
      ) : (
        decider && <p className="comms-decision-summary comms-decision-elsewhere">Respond when the GM puts this fight on the stage.</p>
      )}
    </div>
  );
}
